import { useTranslation } from 'react-i18next';
import { whatsappDeepLink, type Invoice } from '@tailoring-crm/shared';
import { formatCurrency } from '../format.js';
import { useInvoiceDownload } from './use-invoices.js';

/**
 * The bill, sent from the shop's own phone — for the shop that has not connected WhatsApp, and
 * for the one that has but wants it to come from its own number.
 *
 * Two things happen on the one tap. The customer's chat opens with the message already typed,
 * and the PDF downloads onto the device, ready to attach. The chat is opened FIRST and
 * synchronously, inside the tap itself: a window opened after an `await` is a popup to every
 * browser's blocker, and the whole point of the button is the chat.
 *
 * The download lands in the current tab, which is safe because the stored file is served as an
 * attachment — the page does not navigate away.
 */
export function useShareInvoiceByHand(): {
  share: (invoice: Invoice) => Promise<void>;
  isPending: boolean;
} {
  const { t } = useTranslation();
  const download = useInvoiceDownload();

  const share = async (invoice: Invoice): Promise<void> => {
    const text = t('invoices.whatsappCaption', {
      shop: invoice.shop.name,
      number: invoice.number,
      order: invoice.orderNumber,
      total: formatCurrency(invoice.totals.totalPaise),
      balance: formatCurrency(invoice.balancePaise),
    });
    window.open(whatsappDeepLink(invoice.customer.phone, text), '_blank', 'noopener,noreferrer');
    const link = await download.mutateAsync({ invoiceId: invoice.id, paperSize: 'a4' });
    window.location.assign(link.url);
  };

  return { share, isPending: download.isPending };
}
